import type { ReactElement } from 'react';
import { Button, cn, useI18n, useToast } from '@xdocs/ui';
import { usePushSettings } from './usePushSettings.js';
import styles from './PushSettingsSection.module.css';

/**
 * F22 — Web push permission section (exported for `apps/web/src/routes/
 * settings/**` to mount; see `./WIRING.md` for the exact insertion point).
 * Explicit opt-in ONLY (ux.md: "never nag on load") — the browser's native
 * permission prompt fires ONLY from the `enable` button's click handler,
 * never on mount/effect. Implements the full E19 state set: loading
 * (skeleton), the three distinct "off" states (unsupported / not-yet-
 * enabled / denied), the enabled state, and action idle/in-flight/error.
 */
export function PushSettingsSection(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { support, permission, subscribed, actionState, errorReason, enable, disable } = usePushSettings();

  async function handleEnable(): Promise<void> {
    try {
      await enable();
      toast.push({ tone: 'success', message: t('push:toast.enabled') });
    } catch {
      toast.push({ tone: 'error', message: t('push:toast.enableFailed') });
    }
  }

  async function handleDisable(): Promise<void> {
    try {
      await disable();
      toast.push({ tone: 'success', message: t('push:toast.disabled') });
    } catch {
      toast.push({ tone: 'error', message: t('push:toast.disableFailed') });
    }
  }

  return (
    <section className={cn(styles['card'])} aria-labelledby="push-settings-title">
      <h2 id="push-settings-title" className={cn(styles['title'])}>
        {t('push:section.title')}
      </h2>
      <p className={cn(styles['description'])}>{t('push:section.description')}</p>

      {support === 'checking' && (
        <div className={cn(styles['skeleton'])} role="status" aria-label={t('push:section.title')} />
      )}

      {support === 'unsupported' && <p className={cn(styles['status'])}>{t('push:state.unsupported.description')}</p>}

      {support === 'supported' && permission === 'denied' && (
        <>
          <p className={cn(styles['status'])} role="alert">
            {t('push:state.denied.description')}
          </p>
          <p className={cn(styles['status'])}>{t('push:state.denied.instructions')}</p>
        </>
      )}

      {support === 'supported' && permission !== 'denied' && (
        <>
          <p className={cn(styles['status'])}>
            {subscribed ? t('push:state.enabled.description') : t('push:state.disabled.description')}
          </p>

          {actionState === 'error' && errorReason === 'no-registration' && (
            <p className={cn(styles['error'])} role="alert">
              {t('push:state.unavailable.description')}
            </p>
          )}

          <div className={cn(styles['actions'])}>
            {subscribed ? (
              <Button
                variant="secondary"
                state={actionState === 'disabling' ? 'loading' : 'idle'}
                onClick={() => void handleDisable()}
              >
                {t('push:action.disable')}
              </Button>
            ) : (
              <Button
                variant="primary"
                state={actionState === 'enabling' ? 'loading' : 'idle'}
                onClick={() => void handleEnable()}
              >
                {t('push:action.enable')}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
